/**
 * Ros Mascots — animated SVG characters for the Ros chat UI.
 *
 * Registry-based: each agent maps to a mascot. Adding a new agent =
 * one SVG component + one line in MASCOT_REGISTRY.
 *
 * Characters:
 * - Echo (speech bubble) — default/general
 * - Bit (blue robot) — website builder
 * - Quill (clipboard face) — authoring
 * - Tank (silver robot) — discovery
 * - Sentry (shield) — survey guard
 * - Vox (mic robot) — transcription
 * - Audi (headphones) — interview notetaker
 * - Blaze (flame) — questionnaire builder
 * - Kit (fox) — crosstabs builder
 */

import React from 'react'
import {Box} from '@mui/material'

// ── Types ─────────────────────────────────────────────────────────────────────

interface MascotEntry {
  animated: React.FC<{size?: number}>
  avatar: React.FC<{size?: number}>
  /** Tailless avatar — the same mascot WITHOUT the chat speech-bubble tail, on a square viewBox. Used
      for profile/account avatars (where the chat tail looks out of place). */
  avatarPlain: React.FC<{size?: number}>
  name: string
  intro: string
  exit: string
}

// ── Shared keyframes (spread into each component's sx) ────────────────────────

const SHARED_KF = {
  '@keyframes rosFloat': { '0%,100%': {transform: 'translateY(4px)'}, '50%': {transform: 'translateY(-4px)'} },
  '@keyframes rosBob': { '0%,100%': {transform: 'translateY(3.5px)'}, '50%': {transform: 'translateY(-3.5px)'} },
  '@keyframes rosBlink': { '0%,92%,100%': {transform: 'scaleY(1)'}, '96%': {transform: 'scaleY(0.1)'} },
  '@keyframes rosLook': { '0%,20%': {transform: 'translateX(0)'}, '30%,45%': {transform: 'translateX(4px)'}, '55%,70%': {transform: 'translateX(-4px)'}, '80%,100%': {transform: 'translateX(0)'} },
}

const ECHO_KF = {
  ...SHARED_KF,
  '@keyframes echoSmile': { '0%,28%': {opacity: 1}, '33%,95%': {opacity: 0}, '100%': {opacity: 1} },
  '@keyframes echoWow': { '0%,33%': {opacity: 0}, '38%,60%': {opacity: 1}, '65%,100%': {opacity: 0} },
  '@keyframes echoGrin': { '0%,63%': {opacity: 0}, '68%,92%': {opacity: 1}, '97%,100%': {opacity: 0} },
  '@keyframes echoBlush': { '0%,30%': {opacity: 0.25}, '50%': {opacity: 0.7}, '70%,100%': {opacity: 0.25} },
  '@keyframes echoSquint': { '0%,63%': {transform: 'scaleY(1)'}, '70%,90%': {transform: 'scaleY(0.55)'}, '96%,100%': {transform: 'scaleY(1)'} },
}

const BIT_KF = {
  ...SHARED_KF,
  '@keyframes bitArmL': { '0%,100%': {transform: 'rotate(0)'}, '50%': {transform: 'rotate(-16deg)'} },
  '@keyframes bitArmR': { '0%,100%': {transform: 'rotate(0)'}, '50%': {transform: 'rotate(16deg)'} },
  '@keyframes bitStepL': { '0%,100%': {transform: 'translateY(0)'}, '50%': {transform: 'translateY(-5px)'} },
  '@keyframes bitStepR': { '0%,100%': {transform: 'translateY(-5px)'}, '50%': {transform: 'translateY(0)'} },
}

const QUILL_KF = {
  ...SHARED_KF,
  '@keyframes quillClip': { '0%,100%': {transform: 'rotate(-6deg)'}, '50%': {transform: 'rotate(6deg)'} },
  '@keyframes quillCheck': { '0%,38%': {opacity: 1, transform: 'scale(1)'}, '50%,84%': {opacity: 0, transform: 'scale(0.3)'}, '98%,100%': {opacity: 1, transform: 'scale(1)'} },
  '@keyframes quillSmile': { '0%,40%': {opacity: 1}, '52%,84%': {opacity: 0}, '98%,100%': {opacity: 1} },
  '@keyframes quillFrown': { '0%,46%': {opacity: 0}, '58%,82%': {opacity: 1}, '96%,100%': {opacity: 0} },
}

const TANK_KF = {
  ...SHARED_KF,
  '@keyframes tankSway': { '0%,100%': {transform: 'rotate(-8deg)'}, '50%': {transform: 'rotate(8deg)'} },
  '@keyframes tankSwingL': { '0%,100%': {transform: 'rotate(18deg)'}, '50%': {transform: 'rotate(-26deg)'} },
  '@keyframes tankSwingR': { '0%,100%': {transform: 'rotate(-18deg)'}, '50%': {transform: 'rotate(26deg)'} },
  '@keyframes tankPulse': { '0%,100%': {opacity: 0.45}, '50%': {opacity: 1} },
  '@keyframes tankGlow': { '0%,100%': {transform: 'scale(1)'}, '50%': {transform: 'scale(1.45)'} },
}

const RAY_KF = {
  ...SHARED_KF,
  '@keyframes raySpin': { from: {transform: 'rotate(0deg)'}, to: {transform: 'rotate(360deg)'} },
  '@keyframes rayPulse': { '0%,100%': {transform: 'scale(1)'}, '50%': {transform: 'scale(1.08)'} },
}

const POP_KF = {
  ...SHARED_KF,
  '@keyframes popWiggle': { '0%,100%': {transform: 'rotate(-12deg)'}, '50%': {transform: 'rotate(12deg)'} },
}

const SENTRY_KF = {
  ...SHARED_KF,
  '@keyframes sentryAura': { '0%': {transform: 'scale(0.8)', opacity: 0.55}, '100%': {transform: 'scale(1.55)', opacity: 0} },
  '@keyframes sentryEmblem': { '0%,100%': {opacity: 0.6, transform: 'scale(1)'}, '50%': {opacity: 1, transform: 'scale(1.12)'} },
}

const VOX_KF = {
  ...SHARED_KF,
  '@keyframes voxTalk': { '0%': {transform: 'scaleY(0.25)'}, '15%': {transform: 'scaleY(1)'}, '30%': {transform: 'scaleY(0.4)'}, '45%': {transform: 'scaleY(0.95)'}, '60%': {transform: 'scaleY(0.3)'}, '78%': {transform: 'scaleY(0.8)'}, '100%': {transform: 'scaleY(0.3)'} },
  '@keyframes voxEmit': { '0%': {transform: 'scale(0.55)', opacity: 0.9}, '100%': {transform: 'scale(1.5)', opacity: 0} },
}

const AUDI_KF = {
  ...SHARED_KF,
  '@keyframes audiCupPulse': { '0%,100%': {transform: 'scale(1)', opacity: 0.95}, '50%': {transform: 'scale(1.5)', opacity: 0.4} },
  '@keyframes audiWave': { '0%': {opacity: 0}, '45%': {opacity: 0.9}, '100%': {opacity: 0} },
}

const BLAZE_KF = {
  ...SHARED_KF,
  '@keyframes blazeFlicker': { '0%,100%': {transform: 'scaleY(1) scaleX(1)'}, '20%': {transform: 'scaleY(1.06) scaleX(0.96)'}, '40%': {transform: 'scaleY(0.95) scaleX(1.04)'}, '60%': {transform: 'scaleY(1.05) scaleX(0.98)'}, '80%': {transform: 'scaleY(0.98) scaleX(1.02)'} },
  '@keyframes blazeCore': { '0%,100%': {opacity: 0.45, transform: 'scaleY(1)'}, '50%': {opacity: 0.85, transform: 'scaleY(1.14)'} },
  '@keyframes blazeEmber': { '0%': {opacity: 0, transform: 'translateY(0) scale(1)'}, '18%': {opacity: 0.95}, '100%': {opacity: 0, transform: 'translateY(-36px) scale(0.3)'} },
}

const KIT_KF = {
  ...SHARED_KF,
  '@keyframes kitTilt': { '0%,100%': {transform: 'rotate(-5deg)'}, '50%': {transform: 'rotate(5deg)'} },
  '@keyframes kitEarL': { '0%,78%,100%': {transform: 'rotate(0)'}, '85%': {transform: 'rotate(-16deg)'}, '92%': {transform: 'rotate(0)'} },
  '@keyframes kitEarR': { '0%,68%,100%': {transform: 'rotate(0)'}, '75%': {transform: 'rotate(16deg)'}, '82%': {transform: 'rotate(0)'} },
  '@keyframes kitWink': { '0%,86%,100%': {transform: 'scaleY(1)'}, '91%': {transform: 'scaleY(0.1)'} },
}

// Helper for inline animation style
const anim = (name: string, duration: string, delay = '') =>
  ({animation: `${name} ${duration} ease-in-out infinite ${delay}`.trim()})
const tfb = {transformBox: 'fill-box' as any, transformOrigin: 'center'}
const tfbTop = {transformBox: 'fill-box' as any, transformOrigin: 'top center'}
const tfbBottom = {transformBox: 'fill-box' as any, transformOrigin: 'bottom center'}

// ── ECHO ──────────────────────────────────────────────────────────────────────

const AnimatedEcho: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 168" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.12, display: 'block', ...ECHO_KF}}>
    <defs>
      <linearGradient id="echoG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <path d="M120 142 L134 164 L96 146" fill="#0B1220"/>
    <g style={anim('rosFloat', '2.6s')}>
      <rect x="15" y="25" width="120" height="100" rx="40" fill="#FFF"/>
      <ellipse cx="42" cy="83" rx="8" ry="5.5" fill="#4C9BFF" style={anim('echoBlush', '6s')}/>
      <ellipse cx="108" cy="83" rx="8" ry="5.5" fill="#4C9BFF" style={anim('echoBlush', '6s')}/>
      <g style={anim('rosLook', '5s')}>
        <g style={{...tfb, ...anim('echoSquint', '6s')}}>
          <g style={{...tfb, ...anim('rosBlink', '3.4s')}}>
            <circle cx="60" cy="63" r="11" fill="#0B1220"/><circle cx="63" cy="59" r="3.5" fill="#FFF"/>
          </g>
          <g style={{...tfb, ...anim('rosBlink', '3.4s', '0.06s')}}>
            <circle cx="90" cy="63" r="11" fill="#0B1220"/><circle cx="93" cy="59" r="3.5" fill="#FFF"/>
          </g>
        </g>
      </g>
      <path d="M64 85 q11 11 22 0" stroke="#0B1220" strokeWidth="4.5" fill="none" strokeLinecap="round" style={anim('echoSmile', '6s')}/>
      <ellipse cx="75" cy="89" rx="7" ry="9" fill="#0B1220" style={anim('echoWow', '6s')}/>
      <path d="M62 83 q13 16 26 0 q-13 5.5 -26 0 Z" fill="#0B1220" style={anim('echoGrin', '6s')}/>
    </g>
  </Box>
)

const EchoAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 168" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.12, display: 'block', flexShrink: 0}}>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <path d="M120 142 L134 164 L96 146" fill="#0B1220"/>
    <rect x="15" y="25" width="120" height="100" rx="40" fill="#FFF"/>
    <circle cx="60" cy="63" r="11" fill="#0B1220"/><circle cx="90" cy="63" r="11" fill="#0B1220"/>
    <circle cx="63" cy="59" r="3.5" fill="#FFF"/><circle cx="93" cy="59" r="3.5" fill="#FFF"/>
    <ellipse cx="42" cy="83" rx="8" ry="5.5" fill="#4C9BFF" opacity="0.35"/>
    <ellipse cx="108" cy="83" rx="8" ry="5.5" fill="#4C9BFF" opacity="0.35"/>
    <path d="M64 85 q11 11 22 0" stroke="#0B1220" strokeWidth="4.5" fill="none" strokeLinecap="round"/>
  </Box>
)

// ── BIT ───────────────────────────────────────────────────────────────────────

const AnimatedBit: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...BIT_KF}}>
    <defs>
      <linearGradient id="bitG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosBob', '1.7s')}>
      <rect x="12" y="58" width="18" height="15" rx="3" fill="url(#bitG)" style={{...tfbTop, ...anim('bitArmL', '1.1s')}}/>
      <rect x="120" y="58" width="18" height="15" rx="3" fill="url(#bitG)" style={{...tfbTop, ...anim('bitArmR', '1.1s')}}/>
      <rect x="28" y="12" width="94" height="76" rx="11" fill="url(#bitG)"/>
      <rect x="38" y="22" width="74" height="9" rx="4.5" fill="#FFF" opacity="0.22"/>
      <g style={anim('rosLook', '5s')}>
        <g style={{...tfb, ...anim('rosBlink', '3.4s')}}>
          <rect x="50" y="40" width="16" height="24" rx="3" fill="#0B1220"/>
          <rect x="52" y="42" width="6" height="8" rx="2" fill="#FFF" opacity="0.9"/>
        </g>
        <g style={{...tfb, ...anim('rosBlink', '3.4s', '0.06s')}}>
          <rect x="84" y="40" width="16" height="24" rx="3" fill="#0B1220"/>
          <rect x="86" y="42" width="6" height="8" rx="2" fill="#FFF" opacity="0.9"/>
        </g>
      </g>
      <rect x="52" y="88" width="16" height="22" rx="3" fill="url(#bitG)" style={{...tfbTop, ...anim('bitStepL', '1.1s')}}/>
      <rect x="82" y="88" width="16" height="22" rx="3" fill="url(#bitG)" style={{...tfbTop, ...anim('bitStepR', '1.1s')}}/>
    </g>
  </Box>
)

const BitAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="bitSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <rect x="12" y="50" width="18" height="15" rx="3" fill="url(#bitSG)"/>
    <rect x="120" y="50" width="18" height="15" rx="3" fill="url(#bitSG)"/>
    <rect x="28" y="8" width="94" height="76" rx="11" fill="url(#bitSG)"/>
    <rect x="38" y="18" width="74" height="9" rx="4.5" fill="#FFF" opacity="0.22"/>
    <rect x="50" y="36" width="16" height="24" rx="3" fill="#0B1220"/>
    <rect x="52" y="38" width="6" height="8" rx="2" fill="#FFF" opacity="0.9"/>
    <rect x="84" y="36" width="16" height="24" rx="3" fill="#0B1220"/>
    <rect x="86" y="38" width="6" height="8" rx="2" fill="#FFF" opacity="0.9"/>
    <rect x="52" y="84" width="16" height="22" rx="3" fill="url(#bitSG)"/>
    <rect x="82" y="84" width="16" height="22" rx="3" fill="url(#bitSG)"/>
  </Box>
)

// ── QUILL (Authoring) ─────────────────────────────────────────────────────────

const AnimatedQuill: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...QUILL_KF}}>
    <defs>
      <linearGradient id="quillG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosBob', '2.1s')}>
      <g style={{...tfbBottom, ...anim('quillClip', '2.4s')}}>
        <rect x="52" y="6" width="46" height="19" rx="8" fill="#2563EB"/>
        <circle cx="75" cy="15.5" r="4" fill="#0B1220"/>
      </g>
      <rect x="18" y="22" width="114" height="110" rx="38" fill="url(#quillG)"/>
      <rect x="42" y="52" width="26" height="26" rx="8" fill="#FFF"/>
      <path d="M48 66 l5 5.5 l9-11" stroke="#2563EB" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"
        style={{...tfb, ...anim('quillCheck', '3.6s')}}/>
      <rect x="82" y="52" width="26" height="26" rx="8" fill="#FFF"/>
      <path d="M88 66 l5 5.5 l9-11" stroke="#2563EB" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"
        style={{...tfb, ...anim('quillCheck', '3.6s')}}/>
      <path d="M52 96 q23 19 46 0" stroke="#FFF" strokeWidth="6" fill="none" strokeLinecap="round"
        style={anim('quillSmile', '3.6s')}/>
      <path d="M56 100 H94" stroke="#FFF" strokeWidth="6" fill="none" strokeLinecap="round"
        style={anim('quillFrown', '3.6s')}/>
    </g>
  </Box>
)

const QuillAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="quillSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <rect x="52" y="4" width="46" height="19" rx="8" fill="#2563EB"/>
    <circle cx="75" cy="13.5" r="4" fill="#0B1220"/>
    <rect x="18" y="20" width="114" height="110" rx="38" fill="url(#quillSG)"/>
    <rect x="42" y="50" width="26" height="26" rx="8" fill="#FFF"/>
    <path d="M48 64 l5 5.5 l9-11" stroke="#2563EB" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="82" y="50" width="26" height="26" rx="8" fill="#FFF"/>
    <path d="M88 64 l5 5.5 l9-11" stroke="#2563EB" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M52 94 q23 19 46 0" stroke="#FFF" strokeWidth="6" fill="none" strokeLinecap="round"/>
  </Box>
)

// ── TANK (Discovery) ──────────────────────────────────────────────────────────

const AnimatedTank: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...TANK_KF}}>
    <defs>
      <linearGradient id="tankG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#94A3B8"/><stop offset="1" stopColor="#475569"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosBob', '2.0s')}>
      <rect x="42" y="118" width="66" height="18" rx="9" fill="#334155"/>
      <rect x="47" y="38" width="56" height="76" rx="15" fill="url(#tankG)"/>
      <rect x="30" y="62" width="12" height="34" rx="6" fill="#475569" style={{...tfbTop, ...anim('tankSwingL', '1.25s')}}/>
      <rect x="108" y="62" width="12" height="34" rx="6" fill="#475569" style={{...tfbTop, ...anim('tankSwingR', '1.25s')}}/>
      <rect x="57" y="52" width="36" height="22" rx="8" fill="#0B1220"/>
      <g style={{...tfb, ...anim('rosBlink', '3.4s')}}>
        <rect x="63" y="60" width="8" height="5" rx="2.5" fill="#34D399"/>
        <rect x="79" y="60" width="8" height="5" rx="2.5" fill="#34D399"/>
      </g>
      <circle cx="75" cy="92" r="6" fill="#34D399" style={anim('tankPulse', '1.5s')}/>
      <g style={{...tfbBottom, ...anim('tankSway', '2.2s')}}>
        <line x1="75" y1="38" x2="75" y2="22" stroke="#94A3B8" strokeWidth="3.5" strokeLinecap="round"/>
        <circle cx="75" cy="18" r="5" fill="#34D399" style={{...tfb, ...anim('tankGlow', '1.5s')}}/>
      </g>
    </g>
  </Box>
)

const TankAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="tankSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#94A3B8"/><stop offset="1" stopColor="#475569"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <rect x="42" y="112" width="66" height="18" rx="9" fill="#334155"/>
    <rect x="47" y="32" width="56" height="76" rx="15" fill="url(#tankSG)"/>
    <rect x="30" y="56" width="12" height="34" rx="6" fill="#475569"/>
    <rect x="108" y="56" width="12" height="34" rx="6" fill="#475569"/>
    <rect x="57" y="46" width="36" height="22" rx="8" fill="#0B1220"/>
    <rect x="63" y="54" width="8" height="5" rx="2.5" fill="#34D399"/>
    <rect x="79" y="54" width="8" height="5" rx="2.5" fill="#34D399"/>
    <circle cx="75" cy="86" r="6" fill="#34D399"/>
    <line x1="75" y1="32" x2="75" y2="18" stroke="#94A3B8" strokeWidth="3.5" strokeLinecap="round"/>
    <circle cx="75" cy="14" r="5" fill="#34D399"/>
  </Box>
)

// ── RAY (DataTalk) ───────────────────────────────────────────────────────────

const AnimatedRay: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...RAY_KF}}>
    <defs>
      <linearGradient id="rayG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FDE047"/><stop offset="1" stopColor="#FB923C"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosBob', '2.6s')}>
      <g style={{...tfb, animation: 'raySpin 16s linear infinite'}}>
        <g style={{...tfb, ...anim('rayPulse', '2.2s')}}>
          <g stroke="url(#rayG)" strokeWidth="3" strokeLinecap="round">
            <line x1="100" y1="68" x2="110" y2="68"/>
            <line x1="93" y1="89" x2="100" y2="96"/>
            <line x1="75" y1="96" x2="75" y2="106"/>
            <line x1="57" y1="89" x2="50" y2="96"/>
            <line x1="50" y1="68" x2="40" y2="68"/>
            <line x1="57" y1="47" x2="50" y2="40"/>
            <line x1="75" y1="40" x2="75" y2="30"/>
            <line x1="93" y1="47" x2="100" y2="40"/>
          </g>
        </g>
      </g>
      <circle cx="75" cy="68" r="28" fill="url(#rayG)"/>
      <g style={{...tfb, ...anim('rosBlink', '3.6s')}}>
        <circle cx="67" cy="66" r="5" fill="#7A3E00"/><circle cx="83" cy="66" r="5" fill="#7A3E00"/>
        <circle cx="68.2" cy="64" r="1.7" fill="#FFF"/><circle cx="84.2" cy="64" r="1.7" fill="#FFF"/>
      </g>
      <ellipse cx="59" cy="74" rx="4.5" ry="2.5" fill="#F97316" opacity="0.55"/>
      <ellipse cx="91" cy="74" rx="4.5" ry="2.5" fill="#F97316" opacity="0.55"/>
      <path d="M69 76 q6 6 12 0" stroke="#7A3E00" strokeWidth="3" fill="none" strokeLinecap="round"/>
    </g>
  </Box>
)

const RayAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="raySG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FDE047"/><stop offset="1" stopColor="#FB923C"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <g stroke="url(#raySG)" strokeWidth="3" strokeLinecap="round">
      <line x1="100" y1="60" x2="110" y2="60"/><line x1="93" y1="81" x2="100" y2="88"/>
      <line x1="75" y1="88" x2="75" y2="98"/><line x1="57" y1="81" x2="50" y2="88"/>
      <line x1="50" y1="60" x2="40" y2="60"/><line x1="57" y1="39" x2="50" y2="32"/>
      <line x1="75" y1="32" x2="75" y2="22"/><line x1="93" y1="39" x2="100" y2="32"/>
    </g>
    <circle cx="75" cy="60" r="28" fill="url(#raySG)"/>
    <circle cx="67" cy="58" r="5" fill="#7A3E00"/><circle cx="83" cy="58" r="5" fill="#7A3E00"/>
    <circle cx="68.2" cy="56" r="1.7" fill="#FFF"/><circle cx="84.2" cy="56" r="1.7" fill="#FFF"/>
    <ellipse cx="59" cy="66" rx="4.5" ry="2.5" fill="#F97316" opacity="0.55"/>
    <ellipse cx="91" cy="66" rx="4.5" ry="2.5" fill="#F97316" opacity="0.55"/>
    <path d="M69 68 q6 6 12 0" stroke="#7A3E00" strokeWidth="3" fill="none" strokeLinecap="round"/>
  </Box>
)

// ── POP (Custom Agent default) ───────────────────────────────────────────────

const AnimatedPop: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...POP_KF}}>
    <defs>
      <linearGradient id="popG" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#8B5CF6"/><stop offset="1" stopColor="#EC4899"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosFloat', '2.8s')}>
      <g style={{...tfbBottom, ...anim('popWiggle', '1.8s')}}>
        <line x1="75" y1="26" x2="75" y2="16" stroke="#EC4899" strokeWidth="3" strokeLinecap="round"/>
        <circle cx="75" cy="13" r="4" fill="#F9A8D4"/>
      </g>
      <circle cx="75" cy="72" r="38" fill="url(#popG)"/>
      <g style={anim('rosLook', '5s')}>
        <g style={{...tfb, ...anim('rosBlink', '3.4s')}}>
          <circle cx="63" cy="68" r="8" fill="#FFF"/><circle cx="64.5" cy="70" r="4" fill="#2A0F45"/><circle cx="66" cy="66.5" r="1.7" fill="#FFF"/>
        </g>
        <g style={{...tfb, ...anim('rosBlink', '3.4s', '0.06s')}}>
          <circle cx="88" cy="68" r="8" fill="#FFF"/><circle cx="89.5" cy="70" r="4" fill="#2A0F45"/><circle cx="91" cy="66.5" r="1.7" fill="#FFF"/>
        </g>
      </g>
      <path d="M67 84 q8 8 16 0" stroke="#2A0F45" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
    </g>
  </Box>
)

const PopAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="popSG" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#8B5CF6"/><stop offset="1" stopColor="#EC4899"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <line x1="75" y1="18" x2="75" y2="10" stroke="#EC4899" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="75" cy="7" r="4" fill="#F9A8D4"/>
    <circle cx="75" cy="66" r="38" fill="url(#popSG)"/>
    <circle cx="63" cy="62" r="8" fill="#FFF"/><circle cx="64.5" cy="64" r="4" fill="#2A0F45"/><circle cx="66" cy="60.5" r="1.7" fill="#FFF"/>
    <circle cx="88" cy="62" r="8" fill="#FFF"/><circle cx="89.5" cy="64" r="4" fill="#2A0F45"/><circle cx="91" cy="60.5" r="1.7" fill="#FFF"/>
    <path d="M67 78 q8 8 16 0" stroke="#2A0F45" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
  </Box>
)

// ── SENTRY (Survey Guard) ─────────────────────────────────────────────────────

const AnimatedSentry: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...SENTRY_KF}}>
    <defs>
      <linearGradient id="sentryG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#34D399"/><stop offset="1" stopColor="#059669"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosBob', '2.6s')}>
      <circle style={{...tfb, ...anim('sentryAura', '2.4s')}} cx="75" cy="75" r="50" fill="none" stroke="#34D399" strokeWidth="2"/>
      <circle style={{...tfb, animation: 'sentryAura 2.4s ease-out infinite 1.2s'}} cx="75" cy="75" r="50" fill="none" stroke="#34D399" strokeWidth="2"/>
      <path d="M75 22 L108 30 L108 62 C108 86 94 100 75 108 C56 100 42 86 42 62 L42 30 Z" fill="url(#sentryG)"/>
      <path d="M75 22 L108 30 L108 62 C108 86 94 100 75 108 C56 100 42 86 42 62 L42 30 Z" fill="none" stroke="#FFF" strokeWidth="1.2" opacity="0.18"/>
      <path style={{...tfb, ...anim('sentryEmblem', '1.9s')}} d="M66 48 l6 7 l12 -14" stroke="#FFF" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      <g style={{...tfb, ...anim('rosBlink', '3.6s')}}>
        <circle cx="64" cy="72" r="6" fill="#064E3B"/>
        <circle cx="86" cy="72" r="6" fill="#064E3B"/>
        <circle cx="65.5" cy="70" r="2" fill="#FFF"/>
        <circle cx="87.5" cy="70" r="2" fill="#FFF"/>
      </g>
      <path d="M66 84 q9 7 18 0" stroke="#064E3B" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
    </g>
  </Box>
)

const SentryAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="sentrySG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#34D399"/><stop offset="1" stopColor="#059669"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <path d="M75 18 L108 26 L108 58 C108 82 94 96 75 104 C56 96 42 82 42 58 L42 26 Z" fill="url(#sentrySG)"/>
    <path d="M66 44 l6 7 l12 -14" stroke="#FFF" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.85"/>
    <circle cx="64" cy="68" r="6" fill="#064E3B"/><circle cx="86" cy="68" r="6" fill="#064E3B"/>
    <circle cx="65.5" cy="66" r="2" fill="#FFF"/><circle cx="87.5" cy="66" r="2" fill="#FFF"/>
    <path d="M66 80 q9 7 18 0" stroke="#064E3B" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
  </Box>
)

// ── VOX (Transcription) ──────────────────────────────────────────────────────

const AnimatedVox: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...VOX_KF}}>
    <defs>
      <linearGradient id="voxG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosBob', '2.2s')}>
      <circle cx="75" cy="58" r="38" fill="url(#voxG)"/>
      <g style={{...tfb, ...anim('rosBlink', '3.6s')}}>
        <circle cx="65" cy="55" r="5" fill="#06283D"/><circle cx="85" cy="55" r="5" fill="#06283D"/>
        <circle cx="66.5" cy="53.5" r="1.7" fill="#FFF"/><circle cx="86.5" cy="53.5" r="1.7" fill="#FFF"/>
      </g>
      <ellipse style={{...tfb, ...anim('voxTalk', '1.05s')}} cx="75" cy="70" rx="4.5" ry="4.5" fill="#06283D"/>
      {/* Mic */}
      <rect x="68" y="92" width="14" height="22" rx="7" fill="#FFF"/>
      <line x1="72" y1="98" x2="78" y2="98" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="72" y1="103" x2="78" y2="103" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M63 114 a12 12 0 0 0 24 0" fill="none" stroke="#FFF" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="75" y1="126" x2="75" y2="132" stroke="#FFF" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Emit waves */}
      <path style={{...{transformBox:'fill-box' as any, transformOrigin:'left center'}, ...anim('voxEmit', '1.3s')}} d="M87 96 q4 4 0 8" fill="none" stroke="#7DD3FC" strokeWidth="1.5" strokeLinecap="round"/>
      <path style={{transformBox:'fill-box' as any, transformOrigin:'left center', animation:'voxEmit 1.3s ease-out infinite 0.65s'}} d="M87 93 q7 7 0 14" fill="none" stroke="#7DD3FC" strokeWidth="1.5" strokeLinecap="round"/>
    </g>
  </Box>
)

const VoxAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="voxSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <circle cx="75" cy="50" r="38" fill="url(#voxSG)"/>
    <circle cx="65" cy="47" r="5" fill="#06283D"/><circle cx="85" cy="47" r="5" fill="#06283D"/>
    <circle cx="66.5" cy="45.5" r="1.7" fill="#FFF"/><circle cx="86.5" cy="45.5" r="1.7" fill="#FFF"/>
    <ellipse cx="75" cy="62" rx="4.5" ry="3" fill="#06283D"/>
    <rect x="68" y="88" width="14" height="22" rx="7" fill="#FFF"/>
    <path d="M63 110 a12 12 0 0 0 24 0" fill="none" stroke="#FFF" strokeWidth="2.5" strokeLinecap="round"/>
  </Box>
)

// ── AUDI (Interview Notetaker) ───────────────────────────────────────────────

const AnimatedAudi: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...AUDI_KF}}>
    <defs>
      <linearGradient id="audiG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#5EEAD4"/><stop offset="1" stopColor="#14B8A6"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={{animation: 'rosBob 0.95s ease-in-out infinite'}}>
      {/* Sound waves */}
      <g stroke="#5EEAD4" strokeWidth="1.5" fill="none" strokeLinecap="round">
        <path style={{animation: 'audiWave 1.4s ease-in-out infinite 0.5s'}} d="M24 70 q-4 6 0 12"/>
        <path style={{animation: 'audiWave 1.4s ease-in-out infinite 0.25s'}} d="M20 67 q-5 8 0 16"/>
        <path style={{animation: 'audiWave 1.4s ease-in-out infinite'}} d="M16 64 q-6 10 0 20"/>
        <path style={{animation: 'audiWave 1.4s ease-in-out infinite 0.5s'}} d="M126 70 q4 6 0 12"/>
        <path style={{animation: 'audiWave 1.4s ease-in-out infinite 0.25s'}} d="M130 67 q5 8 0 16"/>
        <path style={{animation: 'audiWave 1.4s ease-in-out infinite'}} d="M134 64 q6 10 0 20"/>
      </g>
      {/* Headband */}
      <path d="M36 76 A 39 39 0 0 1 114 76" fill="none" stroke="#FFF" strokeWidth="7" strokeLinecap="round"/>
      <circle cx="75" cy="76" r="36" fill="url(#audiG)"/>
      {/* Ear cups */}
      <rect x="28" y="64" width="12" height="22" rx="6" fill="#FFF"/>
      <rect x="110" y="64" width="12" height="22" rx="6" fill="#FFF"/>
      <circle style={{...tfb, ...anim('audiCupPulse', '1.1s')}} cx="34" cy="75" r="3.5" fill="#14B8A6"/>
      <circle style={{...tfb, ...anim('audiCupPulse', '1.1s')}} cx="116" cy="75" r="3.5" fill="#14B8A6"/>
      {/* Eyes */}
      <g style={{...tfb, ...anim('rosBlink', '3.6s')}}>
        <circle cx="64" cy="73" r="5" fill="#0B3B36"/><circle cx="86" cy="73" r="5" fill="#0B3B36"/>
        <circle cx="65.5" cy="71.5" r="1.7" fill="#FFF"/><circle cx="87.5" cy="71.5" r="1.7" fill="#FFF"/>
      </g>
      <path d="M65 88 q10 7 20 0" stroke="#0B3B36" strokeWidth="2.8" fill="none" strokeLinecap="round"/>
    </g>
  </Box>
)

const AudiAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="audiSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#5EEAD4"/><stop offset="1" stopColor="#14B8A6"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <path d="M36 72 A 39 39 0 0 1 114 72" fill="none" stroke="#FFF" strokeWidth="7" strokeLinecap="round"/>
    <circle cx="75" cy="72" r="36" fill="url(#audiSG)"/>
    <rect x="28" y="60" width="12" height="22" rx="6" fill="#FFF"/>
    <rect x="110" y="60" width="12" height="22" rx="6" fill="#FFF"/>
    <circle cx="64" cy="69" r="5" fill="#0B3B36"/><circle cx="86" cy="69" r="5" fill="#0B3B36"/>
    <circle cx="65.5" cy="67.5" r="1.7" fill="#FFF"/><circle cx="87.5" cy="67.5" r="1.7" fill="#FFF"/>
    <path d="M65 84 q10 7 20 0" stroke="#0B3B36" strokeWidth="2.8" fill="none" strokeLinecap="round"/>
  </Box>
)

// ── BLAZE (Questionnaire Builder) ────────────────────────────────────────────

const AnimatedBlaze: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...BLAZE_KF}}>
    <defs>
      <linearGradient id="blazeG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FF8A5B"/><stop offset="1" stopColor="#FF4D6D"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosBob', '2.0s')}>
      <g style={{...tfbBottom, ...anim('blazeFlicker', '0.7s')}}>
        <path d="M75 18 c20 18 32 32 32 54 a32 32 0 0 1 -64 0 c0 -14 7 -25 15 -34 c2.5 9 8.5 12.5 12.5 13.5 c-5 -15 -1.2 -26 4.5 -33.5 Z" fill="url(#blazeG)"/>
      </g>
      <path style={{...tfbBottom, ...anim('blazeCore', '0.9s')}} d="M75 60 c8.5 7.5 13.5 13.5 13.5 23.5 a13.5 13.5 0 0 1 -27 0 c0 -8.5 6 -16 13.5 -23.5 Z" fill="#FFD9B0"/>
      <g style={{...tfb, ...anim('rosBlink', '3.6s')}}>
        <circle cx="66" cy="76" r="4.5" fill="#5A1020"/><circle cx="84" cy="76" r="4.5" fill="#5A1020"/>
        <circle cx="67.2" cy="74.5" r="1.5" fill="#FFF"/><circle cx="85.2" cy="74.5" r="1.5" fill="#FFF"/>
      </g>
      <path d="M69 85 q6 5 12 0" stroke="#5A1020" strokeWidth="2.8" fill="none" strokeLinecap="round"/>
      <circle style={{...tfb, ...anim('blazeEmber', '1.8s')}} cx="68" cy="14" r="2.5" fill="#FFC078"/>
      <circle style={{...tfb, animation: 'blazeEmber 1.8s ease-in infinite 0.9s'}} cx="82" cy="16" r="2" fill="#FFD9B0"/>
    </g>
  </Box>
)

const BlazeAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="blazeSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FF8A5B"/><stop offset="1" stopColor="#FF4D6D"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <path d="M75 14 c20 18 32 32 32 54 a32 32 0 0 1 -64 0 c0 -14 7 -25 15 -34 c2.5 9 8.5 12.5 12.5 13.5 c-5 -15 -1.2 -26 4.5 -33.5 Z" fill="url(#blazeSG)"/>
    <path d="M75 56 c8.5 7.5 13.5 13.5 13.5 23.5 a13.5 13.5 0 0 1 -27 0 c0 -8.5 6 -16 13.5 -23.5 Z" fill="#FFD9B0"/>
    <circle cx="66" cy="72" r="4.5" fill="#5A1020"/><circle cx="84" cy="72" r="4.5" fill="#5A1020"/>
    <circle cx="67.2" cy="70.5" r="1.5" fill="#FFF"/><circle cx="85.2" cy="70.5" r="1.5" fill="#FFF"/>
    <path d="M69 81 q6 5 12 0" stroke="#5A1020" strokeWidth="2.8" fill="none" strokeLinecap="round"/>
  </Box>
)

// ── KIT (Crosstabs Builder) ──────────────────────────────────────────────────

const AnimatedKit: React.FC<{size?: number}> = ({size = 48}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', ...KIT_KF}}>
    <defs>
      <linearGradient id="kitG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FFA45B"/><stop offset="1" stopColor="#F2683C"/>
      </linearGradient>
    </defs>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <g style={anim('rosFloat', '3s')}>
      <g style={{...{transformBox:'fill-box' as any, transformOrigin:'50% 62%'}, ...anim('kitTilt', '3.4s')}}>
        {/* Ears */}
        <g style={{...tfbBottom, ...anim('kitEarL', '2.6s')}}>
          <polygon points="40,52 55,38 60,60" fill="url(#kitG)"/>
          <polygon points="40,52 55,38 47,56" fill="#C2410C" opacity="0.35"/>
        </g>
        <g style={{...tfbBottom, ...anim('kitEarR', '2.6s')}}>
          <polygon points="110,52 95,38 90,60" fill="url(#kitG)"/>
          <polygon points="110,52 95,38 103,56" fill="#C2410C" opacity="0.35"/>
        </g>
        {/* Face */}
        <polygon points="75,50 112,64 75,110 38,64" fill="url(#kitG)"/>
        <polygon points="75,110 60,80 90,80" fill="#FFE9D6"/>
        {/* Eyes */}
        <g style={{...tfb, ...anim('rosBlink', '3.6s')}}><polygon points="55,70 63,67 60,78" fill="#3A2014"/></g>
        <g style={{...tfb, ...anim('kitWink', '3.4s')}}><polygon points="95,70 87,67 90,78" fill="#3A2014"/></g>
        <circle cx="75" cy="93" r="3.5" fill="#3A2014"/>
      </g>
    </g>
  </Box>
)

const KitAvatar: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 165" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size * 1.1, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="kitSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FFA45B"/><stop offset="1" stopColor="#F2683C"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M134 126 L146 158 L102 130" fill="#0B1220"/>
    <polygon points="40,48 55,34 60,56" fill="url(#kitSG)"/>
    <polygon points="110,48 95,34 90,56" fill="url(#kitSG)"/>
    <polygon points="75,46 112,60 75,106 38,60" fill="url(#kitSG)"/>
    <polygon points="75,106 60,76 90,76" fill="#FFE9D6"/>
    <polygon points="55,66 63,63 60,74" fill="#3A2014"/>
    <polygon points="95,66 87,63 90,74" fill="#3A2014"/>
    <circle cx="75" cy="89" r="3.5" fill="#3A2014"/>
  </Box>
)

// ── TAILLESS AVATARS (profile/account use — same mascots, no chat tail, square viewBox) ──
const EchoAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <rect width="150" height="150" rx="30" fill="#0B1220"/>
    <rect x="15" y="25" width="120" height="100" rx="40" fill="#FFF"/>
    <circle cx="60" cy="63" r="11" fill="#0B1220"/><circle cx="90" cy="63" r="11" fill="#0B1220"/>
    <circle cx="63" cy="59" r="3.5" fill="#FFF"/><circle cx="93" cy="59" r="3.5" fill="#FFF"/>
    <ellipse cx="42" cy="83" rx="8" ry="5.5" fill="#4C9BFF" opacity="0.35"/>
    <ellipse cx="108" cy="83" rx="8" ry="5.5" fill="#4C9BFF" opacity="0.35"/>
    <path d="M64 85 q11 11 22 0" stroke="#0B1220" strokeWidth="4.5" fill="none" strokeLinecap="round"/>
  </Box>
)

const BitAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="bitSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <rect x="12" y="50" width="18" height="15" rx="3" fill="url(#bitSG)"/>
    <rect x="120" y="50" width="18" height="15" rx="3" fill="url(#bitSG)"/>
    <rect x="28" y="8" width="94" height="76" rx="11" fill="url(#bitSG)"/>
    <rect x="38" y="18" width="74" height="9" rx="4.5" fill="#FFF" opacity="0.22"/>
    <rect x="50" y="36" width="16" height="24" rx="3" fill="#0B1220"/>
    <rect x="52" y="38" width="6" height="8" rx="2" fill="#FFF" opacity="0.9"/>
    <rect x="84" y="36" width="16" height="24" rx="3" fill="#0B1220"/>
    <rect x="86" y="38" width="6" height="8" rx="2" fill="#FFF" opacity="0.9"/>
    <rect x="52" y="84" width="16" height="22" rx="3" fill="url(#bitSG)"/>
    <rect x="82" y="84" width="16" height="22" rx="3" fill="url(#bitSG)"/>
  </Box>
)

const QuillAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="quillSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <rect x="52" y="4" width="46" height="19" rx="8" fill="#2563EB"/>
    <circle cx="75" cy="13.5" r="4" fill="#0B1220"/>
    <rect x="18" y="20" width="114" height="110" rx="38" fill="url(#quillSG)"/>
    <rect x="42" y="50" width="26" height="26" rx="8" fill="#FFF"/>
    <path d="M48 64 l5 5.5 l9-11" stroke="#2563EB" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="82" y="50" width="26" height="26" rx="8" fill="#FFF"/>
    <path d="M88 64 l5 5.5 l9-11" stroke="#2563EB" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M52 94 q23 19 46 0" stroke="#FFF" strokeWidth="6" fill="none" strokeLinecap="round"/>
  </Box>
)

const TankAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="tankSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#94A3B8"/><stop offset="1" stopColor="#475569"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <rect x="42" y="112" width="66" height="18" rx="9" fill="#334155"/>
    <rect x="47" y="32" width="56" height="76" rx="15" fill="url(#tankSG)"/>
    <rect x="30" y="56" width="12" height="34" rx="6" fill="#475569"/>
    <rect x="108" y="56" width="12" height="34" rx="6" fill="#475569"/>
    <rect x="57" y="46" width="36" height="22" rx="8" fill="#0B1220"/>
    <rect x="63" y="54" width="8" height="5" rx="2.5" fill="#34D399"/>
    <rect x="79" y="54" width="8" height="5" rx="2.5" fill="#34D399"/>
    <circle cx="75" cy="86" r="6" fill="#34D399"/>
    <line x1="75" y1="32" x2="75" y2="18" stroke="#94A3B8" strokeWidth="3.5" strokeLinecap="round"/>
    <circle cx="75" cy="14" r="5" fill="#34D399"/>
  </Box>
)

const RayAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="raySG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FDE047"/><stop offset="1" stopColor="#FB923C"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <g stroke="url(#raySG)" strokeWidth="3" strokeLinecap="round">
      <line x1="100" y1="60" x2="110" y2="60"/><line x1="93" y1="81" x2="100" y2="88"/>
      <line x1="75" y1="88" x2="75" y2="98"/><line x1="57" y1="81" x2="50" y2="88"/>
      <line x1="50" y1="60" x2="40" y2="60"/><line x1="57" y1="39" x2="50" y2="32"/>
      <line x1="75" y1="32" x2="75" y2="22"/><line x1="93" y1="39" x2="100" y2="32"/>
    </g>
    <circle cx="75" cy="60" r="28" fill="url(#raySG)"/>
    <circle cx="67" cy="58" r="5" fill="#7A3E00"/><circle cx="83" cy="58" r="5" fill="#7A3E00"/>
    <circle cx="68.2" cy="56" r="1.7" fill="#FFF"/><circle cx="84.2" cy="56" r="1.7" fill="#FFF"/>
    <ellipse cx="59" cy="66" rx="4.5" ry="2.5" fill="#F97316" opacity="0.55"/>
    <ellipse cx="91" cy="66" rx="4.5" ry="2.5" fill="#F97316" opacity="0.55"/>
    <path d="M69 68 q6 6 12 0" stroke="#7A3E00" strokeWidth="3" fill="none" strokeLinecap="round"/>
  </Box>
)

const PopAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="popSG" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#8B5CF6"/><stop offset="1" stopColor="#EC4899"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <line x1="75" y1="18" x2="75" y2="10" stroke="#EC4899" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="75" cy="7" r="4" fill="#F9A8D4"/>
    <circle cx="75" cy="66" r="38" fill="url(#popSG)"/>
    <circle cx="63" cy="62" r="8" fill="#FFF"/><circle cx="64.5" cy="64" r="4" fill="#2A0F45"/><circle cx="66" cy="60.5" r="1.7" fill="#FFF"/>
    <circle cx="88" cy="62" r="8" fill="#FFF"/><circle cx="89.5" cy="64" r="4" fill="#2A0F45"/><circle cx="91" cy="60.5" r="1.7" fill="#FFF"/>
    <path d="M67 78 q8 8 16 0" stroke="#2A0F45" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
  </Box>
)

const SentryAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="sentrySG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#34D399"/><stop offset="1" stopColor="#059669"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M75 18 L108 26 L108 58 C108 82 94 96 75 104 C56 96 42 82 42 58 L42 26 Z" fill="url(#sentrySG)"/>
    <path d="M66 44 l6 7 l12 -14" stroke="#FFF" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.85"/>
    <circle cx="64" cy="68" r="6" fill="#064E3B"/><circle cx="86" cy="68" r="6" fill="#064E3B"/>
    <circle cx="65.5" cy="66" r="2" fill="#FFF"/><circle cx="87.5" cy="66" r="2" fill="#FFF"/>
    <path d="M66 80 q9 7 18 0" stroke="#064E3B" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
  </Box>
)

const VoxAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="voxSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4C9BFF"/><stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <circle cx="75" cy="50" r="38" fill="url(#voxSG)"/>
    <circle cx="65" cy="47" r="5" fill="#06283D"/><circle cx="85" cy="47" r="5" fill="#06283D"/>
    <circle cx="66.5" cy="45.5" r="1.7" fill="#FFF"/><circle cx="86.5" cy="45.5" r="1.7" fill="#FFF"/>
    <ellipse cx="75" cy="62" rx="4.5" ry="3" fill="#06283D"/>
    <rect x="68" y="88" width="14" height="22" rx="7" fill="#FFF"/>
    <path d="M63 110 a12 12 0 0 0 24 0" fill="none" stroke="#FFF" strokeWidth="2.5" strokeLinecap="round"/>
  </Box>
)

const AudiAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="audiSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#5EEAD4"/><stop offset="1" stopColor="#14B8A6"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M36 72 A 39 39 0 0 1 114 72" fill="none" stroke="#FFF" strokeWidth="7" strokeLinecap="round"/>
    <circle cx="75" cy="72" r="36" fill="url(#audiSG)"/>
    <rect x="28" y="60" width="12" height="22" rx="6" fill="#FFF"/>
    <rect x="110" y="60" width="12" height="22" rx="6" fill="#FFF"/>
    <circle cx="64" cy="69" r="5" fill="#0B3B36"/><circle cx="86" cy="69" r="5" fill="#0B3B36"/>
    <circle cx="65.5" cy="67.5" r="1.7" fill="#FFF"/><circle cx="87.5" cy="67.5" r="1.7" fill="#FFF"/>
    <path d="M65 84 q10 7 20 0" stroke="#0B3B36" strokeWidth="2.8" fill="none" strokeLinecap="round"/>
  </Box>
)

const BlazeAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="blazeSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FF8A5B"/><stop offset="1" stopColor="#FF4D6D"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <path d="M75 14 c20 18 32 32 32 54 a32 32 0 0 1 -64 0 c0 -14 7 -25 15 -34 c2.5 9 8.5 12.5 12.5 13.5 c-5 -15 -1.2 -26 4.5 -33.5 Z" fill="url(#blazeSG)"/>
    <path d="M75 56 c8.5 7.5 13.5 13.5 13.5 23.5 a13.5 13.5 0 0 1 -27 0 c0 -8.5 6 -16 13.5 -23.5 Z" fill="#FFD9B0"/>
    <circle cx="66" cy="72" r="4.5" fill="#5A1020"/><circle cx="84" cy="72" r="4.5" fill="#5A1020"/>
    <circle cx="67.2" cy="70.5" r="1.5" fill="#FFF"/><circle cx="85.2" cy="70.5" r="1.5" fill="#FFF"/>
    <path d="M69 81 q6 5 12 0" stroke="#5A1020" strokeWidth="2.8" fill="none" strokeLinecap="round"/>
  </Box>
)

const KitAvatarPlain: React.FC<{size?: number}> = ({size = 32}) => (
  <Box component="svg" viewBox="0 0 150 150" xmlns="http://www.w3.org/2000/svg"
    sx={{width: size, height: size, display: 'block', flexShrink: 0}}>
    <defs>
      <linearGradient id="kitSG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FFA45B"/><stop offset="1" stopColor="#F2683C"/>
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="144" height="132" rx="28" fill="#0B1220"/>
    <polygon points="40,48 55,34 60,56" fill="url(#kitSG)"/>
    <polygon points="110,48 95,34 90,56" fill="url(#kitSG)"/>
    <polygon points="75,46 112,60 75,106 38,60" fill="url(#kitSG)"/>
    <polygon points="75,106 60,76 90,76" fill="#FFE9D6"/>
    <polygon points="55,66 63,63 60,74" fill="#3A2014"/>
    <polygon points="95,66 87,63 90,74" fill="#3A2014"/>
    <circle cx="75" cy="89" r="3.5" fill="#3A2014"/>
  </Box>
)

// ── MASCOT REGISTRY ───────────────────────────────────────────────────────────

const ECHO_ENTRY: MascotEntry = {
  animated: AnimatedEcho, avatar: EchoAvatar, avatarPlain: EchoAvatarPlain,
  name: 'Ros', intro: "I'm Ros — your Rival Group OS.", exit: "Back to Ros.",
}
const BIT_ENTRY: MascotEntry = {
  animated: AnimatedBit, avatar: BitAvatar, avatarPlain: BitAvatarPlain,
  name: 'Bit', intro: "Meet Bit — our website builder. Let's create something great.", exit: "Bit's done — handing back to Ros.",
}
const QUILL_ENTRY: MascotEntry = {
  animated: AnimatedQuill, avatar: QuillAvatar, avatarPlain: QuillAvatarPlain,
  name: 'Quill', intro: "Bringing in Quill — our survey authoring specialist.", exit: "Quill's wrapped up — back to Ros.",
}
const TANK_ENTRY: MascotEntry = {
  animated: AnimatedTank, avatar: TankAvatar, avatarPlain: TankAvatarPlain,
  name: 'Tank', intro: "Tank here - ready to explore and discover insights.", exit: "Tank signing off - back to Ros.",
}
const SENTRY_ENTRY: MascotEntry = {
  animated: AnimatedSentry, avatar: SentryAvatar, avatarPlain: SentryAvatarPlain,
  name: 'Sentry', intro: "Sentry on guard - let's check your survey quality.", exit: "Sentry all clear - back to Ros.",
}
const VOX_ENTRY: MascotEntry = {
  animated: AnimatedVox, avatar: VoxAvatar, avatarPlain: VoxAvatarPlain,
  name: 'Vox', intro: "Vox here - ready to transcribe your recordings.", exit: "Vox signing off - back to Ros.",
}
const AUDI_ENTRY: MascotEntry = {
  animated: AnimatedAudi, avatar: AudiAvatar, avatarPlain: AudiAvatarPlain,
  name: 'Audi', intro: "Audi listening in - I'll capture the key insights.", exit: "Audi wrapped up - back to Ros.",
}
const BLAZE_ENTRY: MascotEntry = {
  animated: AnimatedBlaze, avatar: BlazeAvatar, avatarPlain: BlazeAvatarPlain,
  name: 'Blaze', intro: "Blaze here - let's build your questionnaire.", exit: "Blaze finished - back to Ros.",
}
const KIT_ENTRY: MascotEntry = {
  animated: AnimatedKit, avatar: KitAvatar, avatarPlain: KitAvatarPlain,
  name: 'Kit', intro: "Kit on it - let's plan and build your crosstabs.", exit: "Kit wrapped up - back to Ros.",
}
const RAY_ENTRY: MascotEntry = {
  animated: AnimatedRay, avatar: RayAvatar, avatarPlain: RayAvatarPlain,
  name: 'Ray', intro: "Ray here - let's talk to your data.", exit: "Ray signing off - back to Ros.",
}
const POP_ENTRY: MascotEntry = {
  animated: AnimatedPop, avatar: PopAvatar, avatarPlain: PopAvatarPlain,
  name: 'Pop', intro: "Pop here - ready to help!", exit: "Pop's done - back to Ros.",
}

const MASCOT_REGISTRY: Record<string, MascotEntry> = {
  default:                     ECHO_ENTRY,
  general:                     ECHO_ENTRY,
  general_conversation:        ECHO_ENTRY,
  greeting:                    ECHO_ENTRY,
  website_builder:             BIT_ENTRY,
  website_builder_agent:       BIT_ENTRY,
  authoring:                   QUILL_ENTRY,
  authoring_agent:             QUILL_ENTRY,
  authoring_review:            QUILL_ENTRY,
  survey_guard:                SENTRY_ENTRY,
  survey_guard_agent:          SENTRY_ENTRY,
  transcription:               VOX_ENTRY,
  transcription_agent:         VOX_ENTRY,
  interview_notetaker:         AUDI_ENTRY,
  interview_notetaker_agent:   AUDI_ENTRY,
  questionnaire_builder:       BLAZE_ENTRY,
  questionnaire_builder_agent: BLAZE_ENTRY,
  crosstabs_builder:           KIT_ENTRY,
  crosstabs_builder_agent:     KIT_ENTRY,
  crosstabs:                   KIT_ENTRY,
  crosstabs_agent:             KIT_ENTRY,
  datatalk:                    RAY_ENTRY,
  datatalk_agent:              RAY_ENTRY,
  discovery:                   TANK_ENTRY,
  discovery_agent:             TANK_ENTRY,
  discovery_interviewer_agent: TANK_ENTRY,
  custom:                      POP_ENTRY,
}

/** Resolve mascot components from an agent intent string. Falls back to Echo. */
export function getMascot(intent?: string): MascotEntry {
  return MASCOT_REGISTRY[intent ?? ''] ?? MASCOT_REGISTRY.default
}

/** All available mascot names — for the agent builder mascot picker */
const MASCOT_BY_NAME: Record<string, MascotEntry> = {
  echo: ECHO_ENTRY, bit: BIT_ENTRY, quill: QUILL_ENTRY, tank: TANK_ENTRY,
  sentry: SENTRY_ENTRY, vox: VOX_ENTRY, audi: AUDI_ENTRY, blaze: BLAZE_ENTRY,
  kit: KIT_ENTRY, ray: RAY_ENTRY, pop: POP_ENTRY,
}

/** Resolve mascot by name (e.g. 'bit', 'quill'). Used by agent builder. Falls back to Pop for custom agents. */
export function getMascotByName(name?: string): MascotEntry {
  return MASCOT_BY_NAME[name?.toLowerCase() ?? ''] ?? POP_ENTRY
}

/** List all available mascot names for the picker UI */
export function getAvailableMascots(): Array<{id: string; name: string}> {
  return Object.entries(MASCOT_BY_NAME).map(([id, entry]) => ({id, name: entry.name}))
}

// ── PUBLIC COMPONENTS ─────────────────────────────────────────────────────────

export const RosAvatar: React.FC<{intent?: string; size?: number}> = ({intent, size = 36}) => {
  const {avatar: Avatar} = getMascot(intent)
  return <Avatar size={size} />
}

/** Handoff card — shown when Ros passes control to/from an agent */
export const RosHandoffCard: React.FC<{from: string; to: string}> = ({from, to}) => {
  const toMascot = getMascot(to)
  const fromMascot = getMascot(from)
  const isEntering = to !== 'general' && to !== 'default'
  const mascot = isEntering ? toMascot : fromMascot
  const message = isEntering ? toMascot.intro : fromMascot.exit
  const Animated = mascot.animated

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 2, py: 1.5, mx: 'auto', my: 1,
      bgcolor: 'rgba(11, 18, 32, 0.06)', borderRadius: 3,
      maxWidth: 420,
    }}>
      <Animated size={40} />
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
        <Box component="span" sx={{fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled'}}>
          {isEntering ? `${mascot.name} joining` : `${mascot.name} signing off`}
        </Box>
        <Box component="span" sx={{fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.4}}>
          {message}
        </Box>
      </Box>
    </Box>
  )
}

export { AnimatedEcho, AnimatedBit, AnimatedQuill, AnimatedTank, AnimatedSentry, AnimatedVox, AnimatedAudi, AnimatedBlaze, AnimatedKit, AnimatedRay, AnimatedPop }
export { EchoAvatar, BitAvatar, QuillAvatar, TankAvatar, SentryAvatar, VoxAvatar, AudiAvatar, BlazeAvatar, KitAvatar, RayAvatar, PopAvatar }
